import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import $api from "http/index";
import { listStudents } from "service/StudentsService";
import {
  PageHeader,
  Skeleton,
  EmptyState,
  ErrorState,
  DataTable,
  Card,
} from 'components'
import s from "./DeanDashboard.module.scss";

export default function DeanDashboard() {
  const { t } = useTranslation('common')
  // Wave 4: title в табе браузера для главной страницы декана.
  useEffect(() => {
    document.title = `${t('deanDashboard.dean', 'Декан')} — ЧУ`
  }, [t])
  const [data, setData] = useState({ students: 0, groups: 0, attendance: 0, debts: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = () => {
    setLoading(true);
    setLoadError(false);
    Promise.allSettled([
      listStudents({ status: "active", page_size: 1 }),
      $api.get("groups/"),
      $api.get("students/orders/", { params: { status: "signed", page_size: 5 } }),
    ])
      .then(([stRes, grRes, ordRes]) => {
        let anyOk = false;
        if (stRes.status === 'fulfilled') {
          anyOk = true;
          const r = stRes.value;
          setData((d) => ({ ...d, students: r.data?.count || (r.data?.length ?? 0) }));
        }
        if (grRes.status === 'fulfilled') {
          anyOk = true;
          const list = grRes.value.data?.results || grRes.value.data || [];
          setGroups(list);
          setData((d) => ({ ...d, groups: list.length }));
        }
        if (ordRes.status === 'fulfilled') {
          anyOk = true;
          setRecentOrders(ordRes.value.data?.results || ordRes.value.data || []);
        }
        if (!anyOk) setLoadError(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className={s.page}>
        <Skeleton variant="card" />
        <Skeleton variant="table" rows={5} cols={4} />
        <Skeleton variant="table" rows={5} cols={4} />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={s.page}>
        <ErrorState title={t('deanDash.no_data')} onRetry={load} />
      </div>
    );
  }

  return (
    <div className={s.page}>
      <PageHeader title={t('deanDash.title')} />

      <div className={s.cards}>
        <Card className={s.card}>
          <div className={s.label}>{t('deanDash.active_students')}</div>
          <div className={s.value}>{data.students}</div>
        </Card>
        <Card className={s.card}>
          <div className={s.label}>{t('deanDash.groups')}</div>
          <div className={s.value}>{data.groups}</div>
        </Card>
        <Card className={s.card}>
          <div className={s.label}>{t('deanDash.attendance_avg')}</div>
          <div className={s.value}>{data.attendance || "—"}</div>
        </Card>
        <Card className={s.card}>
          <div className={s.label}>{t('deanDash.debtors')}</div>
          <div className={s.value}>{data.debts || "—"}</div>
        </Card>
      </div>

      <div className={s.section}>
        <h3>{t('deanDash.recent_orders')}</h3>
        {recentOrders.length === 0 ? (
          <EmptyState iconName="folder" title={t('deanDash.no_data')} variant="compact" />
        ) : (
          <DataTable
            columns={[
              { key: 'number', label: t('deanDash.number_short') },
              { key: 'issued_at', label: t('deanDash.date') },
              { key: 'kind_display', label: t('deanDash.kind') },
              { key: 'student_label', label: t('deanDash.student') },
            ]}
            data={recentOrders}
            searchable={false}
            pageSize={10}
            emptyText={t('deanDash.no_data')}
          />
        )}
      </div>

      <div className={s.section}>
        <h3>{t('deanDash.faculty_groups')}</h3>
        {groups.length === 0 ? (
          <EmptyState iconName="users" title={t('deanDash.no_data')} variant="compact" />
        ) : (
          <DataTable
            columns={[
              { key: 'title', label: t('deanDash.group'), sortable: true },
              { key: 'course', label: t('deanDash.course'), sortable: true, render: (v) => v || '—' },
              { key: 'count_students', label: t('deanDash.students_count'), sortable: true, align: 'right' },
              { key: 'curator', label: t('deanDash.curator'), render: (_, r) => r.curator?.full_name || '—' },
            ]}
            data={groups.slice(0, 20)}
            pageSize={20}
            emptyText={t('deanDash.no_data')}
          />
        )}
      </div>
    </div>
  );
}
